/**
 * B.7.e Step 1 — pick a template + target slug.
 */

import { useState } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { testIds } from '../../lib/testIds';
import type { Id } from '../../../convex/_generated/dataModel';
import type { WizardState } from './WizardShell';

interface Step1Props {
  state: WizardState;
  onContinue: (
    templateId: Id<'portalTemplates'>,
    targetClientSlug: string,
  ) => void;
}

const SLUG_RE = /^[a-z0-9-]+$/;

export function Step1Template({ state, onContinue }: Step1Props) {
  const templates = useQuery(api.portalProvisioning.listTemplates, {});
  const [picked, setPicked] = useState<Id<'portalTemplates'> | null>(
    state.templateId,
  );
  const [slug, setSlug] = useState(state.targetClientSlug);
  const slugLower = slug.trim().toLowerCase();
  const slugError =
    slugLower.length === 0
      ? null
      : !SLUG_RE.test(slugLower)
        ? 'use a–z, 0–9, hyphens only'
        : slugLower.length > 63
          ? '≤63 chars'
          : null;
  const canContinue =
    picked !== null && slugError === null && slugLower.length > 0;

  return (
    <section
      data-testid={testIds.portalWizard.templatePicker}
      className="space-y-6"
    >
      <div>
        <h2 className="text-lg font-semibold mb-3">Pick a template</h2>
        {templates === undefined ? (
          <p className="text-sm text-gray-800">Loading templates&hellip;</p>
        ) : templates.length === 0 ? (
          <p className="text-sm text-gray-800">
            No templates available. Run{' '}
            <code className="font-mono">/admin/seedDefaultTemplates</code>{' '}
            to load the corporate-ma and litigation-disputes starters, or
            call{' '}
            <code className="font-mono">portalProvisioning.createTemplate</code>{' '}
            to author a custom one.
          </p>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {templates.map((t) => {
              const selected = picked === t._id;
              return (
                <li key={t._id}>
                  <button
                    type="button"
                    data-testid={testIds.portalWizard.templateOption(t._id)}
                    data-selected={selected ? 'true' : 'false'}
                    onClick={() => setPicked(t._id)}
                    className={`w-full text-left border px-4 py-3 hover:bg-gray-100 ${
                      selected
                        ? 'border-black bg-gray-200'
                        : 'border-black/30 bg-white'
                    }`}
                  >
                    <div className="font-semibold">{t.name}</div>
                    <div className="text-xs text-gray-800 mt-1">
                      {t.practiceArea} · {t.theme} · cloned {t.cloneCount}{' '}
                      times
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div>
        <h2 className="text-lg font-semibold mb-2">Target portal slug</h2>
        <p className="text-sm text-gray-800 mb-2">
          The new portal will live at{' '}
          <code className="font-mono">
            /c/{slugLower.length === 0 ? '<slug>' : slugLower}/desktop
          </code>
          .
        </p>
        <input
          type="text"
          data-testid={testIds.portalWizard.targetSlugInput}
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          placeholder="e.g. acme-legal"
          className="border border-black/60 bg-white px-3 py-2 text-sm w-72 max-w-full"
        />
        {slugError !== null && (
          <p className="text-xs text-red-700 mt-1">{slugError}</p>
        )}
      </div>

      <div className="pt-2">
        <button
          type="button"
          data-testid={testIds.portalWizard.step1Next}
          disabled={!canContinue}
          onClick={() => {
            if (picked !== null) onContinue(picked, slugLower);
          }}
          className="border border-black bg-white px-4 py-2 text-sm disabled:opacity-50 hover:bg-gray-100"
        >
          Next: Upload source docs
        </button>
      </div>
    </section>
  );
}
